import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightsTimeSeriesBucket } from '@brand24-original/llm-types';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';
import { PeriodType } from '../../../../shared/types/prompt/PeriodType';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { getInsightsMetricColors } from '../../helpers/insightsChartColors';
import InsightsTrendChart, { InsightsTrendSeries } from '../insights-trend-chart/InsightsTrendChart';

interface FunnelOverTimeChartProps {
  buckets: InsightsTimeSeriesBucket[] | null;
  period: PeriodType;
  isLoading: boolean;
  isError?: boolean;
}

/**
 * "Funnel over time": the BrandFunnel stages (visibility → Top 3 → Top 1, % of all AI responses)
 * per time bucket, on the SAME time-series payload as VisibilityCitedChart. Colors = the funnel
 * ladder (primary-ux-200/300/500) so the lines match the BrandFunnel bars and FunnelVsCompetitors.
 */
const FunnelOverTimeChart: React.FC<FunnelOverTimeChartProps> = ({
  buckets,
  period,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const colors = useMemo(() => getInsightsMetricColors(theme), [theme]);

  const series: InsightsTrendSeries[] = useMemo(() => {
    const data = buckets ?? [];
    // A StatsCache payload from an older deploy (TTL 90 s) may lack top3/top1 — treat them as 0, as
    // BrandFunnel does, so the chart still draws real visibility instead of failing.
    return [
      {
        name: t('insightPage.funnel.rowVisibility'),
        color: colors.funnelVisibility,
        areaAlpha: 0.28,
        data: data.map((bucket) => percentOf(bucket.visible, bucket.total)),
      },
      {
        name: t('insightPage.funnel.rowTop3'),
        color: colors.funnelTop3,
        areaAlpha: 0.24,
        data: data.map((bucket) => percentOf(bucket.top3 ?? 0, bucket.total)),
      },
      {
        name: t('insightPage.funnel.rowTop1'),
        color: colors.funnelTop1,
        areaAlpha: 0.24,
        data: data.map((bucket) => percentOf(bucket.top1 ?? 0, bucket.total)),
      },
    ];
  }, [buckets, colors, t]);

  return (
    <div className="stats__panel funnel-over-time-chart">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.funnelOverTime.title')}
          <CustomTooltip tooltipText="insightPage.funnelOverTime.tooltip" addMargin={false} />
        </div>
      </div>
      <div className="insights-trend-chart__card">
        {isLoading ? (
          <div className="insights-trend-chart__skeleton" />
        ) : isError ? (
          <EmptyPanel textKey="somethingWentWrong" />
        ) : !buckets || buckets.length === 0 ? (
          <EmptyPanel />
        ) : (
          <InsightsTrendChart buckets={buckets} period={period} series={series} />
        )}
      </div>
    </div>
  );
};

export default FunnelOverTimeChart;
